import { SalesLeadService } from '@http/clients/reporting-app/sales-lead.service';
import { TabNames } from '@models/enums/TabNames';
import { GridDisplayScheme, OverviewLayout } from '@pages/reports/types/report-layout-type.enum';
import { formatNumbersCompact, percentageFormatter, truncateValue } from '@utils/format-functions';
import { AgChartLegendLabelFormatterParams } from 'ag-charts-community';
import { ICellRendererParams } from 'ag-grid-enterprise';
import { IReportConfiguration } from '../../types/report-config.interface';
import { ReportType } from '../../types/report-type.enum';
import { WidgetType } from '../../types/report-widget-type.enum';
import { GridTooltipComponent } from '../report-single-tabs/grid-tab/tooltip/grid-tooltip/grid-tooltip.component';
import {
    accountEditorConfig,
    customerEditorConfig,
    departmentEditorConfig,
    expectedResponseDateEditorConfig,
    includeArchivedEditorConfig,
    marketsEditorConfig,
    opportunityCreationDateEditorConfig,
    opportunityDueDateEditorConfig,
    opportunitySourceEditorConfig,
    potentialValueEditorConfig,
    probabilityEditorConfig,
    projectEndDateEditorConfig,
    projectStartDateEditorConfig
} from './shared/additional-filters';
import { crmMainFilters } from './shared/main-filters';

export const SalesLeads: IReportConfiguration<SalesLeadService> = {
    base: {
        type: ReportType.SalesLeads,
        serviceType: SalesLeadService
    },
    overviewTab: {
        widgets: [
            {
                methodName: 'getSalesLeadBoxesValues',
                type: WidgetType.Box,
                layoutTemplateArea: 'boxes'
            },
            {
                methodName: 'getSalesLeadMetricsValues',
                type: WidgetType.Metrics,
                layoutTemplateArea: 'metrics',
                settings: {
                    cols: [
                        { name: 'avg', label: 'Average' },
                        { name: 'min', label: 'Min' },
                        { name: 'med', label: 'Med' },
                        { name: 'max', label: 'Max' }
                    ],
                    rows: [
                        { name: 'PotentialValue', label: 'Potential Value ($)', format: 'currency' },
                        { name: 'PotentialGrossMarginPercent', label: 'Potential GM (%)', format: 'customPercent' }
                    ]
                }
            },
            {
                methodName: 'getSalesLeadLineChartValues',
                type: WidgetType.Chart,
                layoutTemplateArea: 'chart',
                settings: {
                    title: {
                        text: 'Top 10 Sales Lead by Potential Value'
                    },
                    series: [
                        {
                            type: 'line',
                            xKey: 'leadName',
                            yKey: 'potentialValue',
                            yName: 'Potential Value ($)'
                        },
                        {
                            type: 'line',
                            xKey: 'leadName',
                            yKey: 'potentialGrossMarginPercent',
                            yName: 'Potential GM (%)'
                        }
                    ],
                    axes: [
                        {
                            type: 'category',
                            position: 'bottom',
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    truncateValue(params.value, 15),
                                rotation: 315
                            }
                        },
                        {
                            type: 'number',
                            position: 'left',
                            title: {
                                text: 'Amount'
                            },
                            keys: ['potentialValue'],
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    formatNumbersCompact(params.value)
                            }
                        },
                        {
                            type: 'number',
                            position: 'right',
                            title: {
                                text: 'Percentage'
                            },
                            keys: ['potentialGrossMarginPercent'],
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    percentageFormatter(Number(params.value))
                            }
                        }
                    ]
                }
            },
            {
                methodName: 'getSalesLeadColumnChartValues',
                type: WidgetType.Chart,
                layoutTemplateArea: 'chart2',
                settings: {
                    title: {
                        text: 'Salesperson Performance'
                    },
                    series: [
                        {
                            type: 'bar',
                            xKey: 'salesPerson',
                            yKey: 'totalPotentialValue',
                            yName: 'Total Potential Value ($)'
                        },
                        {
                            type: 'line',
                            xKey: 'salesPerson',
                            yKey: 'totalGMPercent',
                            yName: 'Total Potential GM (%)'
                        }
                    ],
                    axes: [
                        {
                            type: 'category',
                            position: 'bottom',
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    truncateValue(params.value, 15),
                                rotation: 315
                            }
                        },
                        {
                            type: 'number',
                            position: 'left',
                            title: {
                                text: 'Amount'
                            },
                            keys: ['totalPotentialValue'],
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    formatNumbersCompact(params.value)
                            }
                        },
                        {
                            type: 'number',
                            position: 'right',
                            title: {
                                text: 'Percentage'
                            },
                            keys: ['totalGMPercent'],
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    percentageFormatter(Number(params.value))
                            }
                        }
                    ]
                }
            }
        ],
        layout: OverviewLayout.ExtendedTwoPlusOne
    },
    gridTab: {
        widgets: [
            {
                methodName: 'getSalesLeadValues',
                type: WidgetType.Grid,
                layoutTemplateArea: 'grid',
                settings: [
                    {
                        field: 'entityId',
                        headerName: 'OID',
                        lockVisible: true,
                        cellRenderer: (params: ICellRendererParams) => {
                            if (params.value) {
                                const link = `/opportunities-single/${params.value}/${TabNames[TabNames.Overview]}`;
                                return `<a class="anchor" href="${link}" target="_blank" rel="noopener noreferrer">${params.value}</a>`;
                            }
                            return `<div></div>`;
                        }
                    },
                    { field: 'opportunityName', headerName: 'Opportunity Name', enableRowGroup: true },
                    { field: 'creationDate', headerName: 'Creation Date', type: 'date' },
                    { field: 'dueDate', headerName: 'Due Date', type: 'date' },
                    { field: 'expectedBookingDate', headerName: 'Expected Booking Date', type: 'date' },
                    { field: 'customer', headerName: 'Customer', enableRowGroup: true },
                    { field: 'account', headerName: 'Account', enableRowGroup: true },
                    { field: 'potentialValue', headerName: 'Potential Value ($)', type: 'currency', aggFunc: 'sum' },
                    {
                        field: 'potentialGrossMarginPercent',
                        headerName: 'Potential GM (%)',
                        type: 'customPercentage',
                        aggFunc: 'avg'
                    },
                    { field: 'division', headerName: 'Department', enableRowGroup: true },
                    { field: 'salesPerson', headerName: 'Salesperson', enableRowGroup: true },
                    { field: 'status', headerName: 'Status (Unqualified)', enableRowGroup: true },
                    { field: 'probability', headerName: 'Probability', type: 'percentageString', aggFunc: 'avg' },
                    { field: 'speculativeValue', headerName: 'Speculative Value', type: 'currency', aggFunc: 'sum' },
                    { field: 'market', headerName: 'Market' },
                    { field: 'lead', headerName: 'Opportunity Source', enableRowGroup: true },
                    { field: 'organization', headerName: 'Organization', enableRowGroup: true },
                    {
                        field: 'customFieldValues',
                        headerName: 'Custom Fields',
                        tooltipField: 'customFieldValues',
                        tooltipComponent: GridTooltipComponent
                    },
                    { field: 'condition', headerName: 'Condition', enableRowGroup: true },
                    {
                        field: 'opportunityLastUpdatedNote',
                        headerName: 'Last Updated Note',
                        tooltipField: 'opportunityLastUpdatedNote'
                    },
                    { field: 'modificationDate', headerName: 'Modification Date', type: 'date' }
                ]
            }
        ],
        layout: GridDisplayScheme.Standard
    },
    filters: {
        main: [...crmMainFilters],
        additional: [
            opportunityCreationDateEditorConfig,
            opportunityDueDateEditorConfig,
            projectStartDateEditorConfig,
            projectEndDateEditorConfig,
            marketsEditorConfig,
            opportunitySourceEditorConfig,
            departmentEditorConfig,
            customerEditorConfig,
            accountEditorConfig,
            potentialValueEditorConfig,
            probabilityEditorConfig,
            expectedResponseDateEditorConfig,
            includeArchivedEditorConfig
        ]
    }
};
